// Audit Trail → Export (Admins). Date range · person · module · search · bot actions → CSV of every matching row.
import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { auditExportDownloadUrl, countAuditExport, getAuditPeople, requestAuditExportTicket } from "@/lib/api";

const dayStr = (d) => d.toISOString().slice(0, 10);

export function AuditExportDialog({ open, onOpenChange, modules, initialModule = "all", initialQuery = "" }) {
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(dayStr(new Date(today.getTime() - 29 * 86400000)));
  const [to, setTo] = useState(dayStr(today));
  const [people, setPeople] = useState([]);
  const [person, setPerson] = useState("all");
  const [module, setModule] = useState(initialModule);
  const [q, setQ] = useState(initialQuery);
  const [bots, setBots] = useState(true);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModule(initialModule); setQ(initialQuery); setCount(null);
    getAuditPeople(90).then((d) => setPeople(d.people || [])).catch(() => setPeople([]));
  }, [open, initialModule, initialQuery]);

  const params = () => ({
    from, to,
    performed_by: person === "all" ? undefined : person,
    module: module === "all" ? undefined : module,
    q: q.trim() || undefined,
    include_bots: bots,
  });

  const preview = async () => {
    setBusy(true);
    try { setCount(await countAuditExport(params())); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  // The file is streamed by the browser's own download manager (a one-time ticket, no XHR, no timeout) —
  // a 30-day export of the whole team can be tens of thousands of rows.
  const download = async () => {
    setBusy(true);
    try {
      const t = await requestAuditExportTicket(params());
      const a = document.createElement("a");
      a.href = auditExportDownloadUrl(t.ticket);
      a.rel = "noopener";
      a.setAttribute("data-testid", "audit-export-download-link");
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Download started — ${t.filename}. Watch your browser's downloads; big exports take a minute or two.`, { duration: 8000 });
      onOpenChange(false);
    } catch (e) { toast.error("Export failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="audit-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download className="w-4 h-4" /> Export audit trail</DialogTitle>
          <DialogDescription>Every matching row, no cap — one CSV in London time. Bot actions (Autopilot, Mirror Autopilot, Final Checks watch) are tagged <span className="font-mono">bot</span> so they sit beside the clicks.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setCount(null); }} className="h-8 text-xs" data-testid="audit-export-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setCount(null); }} className="h-8 text-xs" data-testid="audit-export-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Person</Label>
            <Select value={person} onValueChange={(v) => { setPerson(v); setCount(null); }}>
              <SelectTrigger className="h-8 text-xs" data-testid="audit-export-person"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone (people + bots)</SelectItem>
                {people.map((p) => <SelectItem key={p.person} value={p.person}>{p.person} · {p.rows}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Module</Label>
            <Select value={module} onValueChange={(v) => { setModule(v); setCount(null); }}>
              <SelectTrigger className="h-8 text-xs" data-testid="audit-export-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {Object.entries(modules).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                <SelectItem value="applications_legacy">Applications (legacy workflow logs)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Search (event, SF, path, details)</Label>
            <Input value={q} onChange={(e) => { setQ(e.target.value); setCount(null); }} placeholder="optional" className="h-8 text-xs" data-testid="audit-export-q" />
          </div>
          <label className="col-span-2 flex items-center justify-between gap-3 rounded-md border px-3 py-2 cursor-pointer">
            <span className="flex items-center gap-2 text-xs"><Bot className="w-3.5 h-3.5 text-slate-500" /> Include bot actions (Application Hub Autopilot · Mirror Autopilot · Final Checks alarms)</span>
            <Switch checked={bots} onCheckedChange={(v) => { setBots(v); setCount(null); }} data-testid="audit-export-bots" />
          </label>
          {count && (
            <p className="col-span-2 text-xs text-muted-foreground" data-testid="audit-export-count">
              <b className="text-slate-800">{count.bots_estimated ? "about " : ""}{count.rows.toLocaleString()}</b> rows · {count.humans.toLocaleString()} by people · {count.bots.toLocaleString()} by bots{count.bots_estimated ? " (estimate)" : ""} · {count.from} → {count.to}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="sm" onClick={preview} disabled={busy} data-testid="audit-export-preview">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Count rows"}
          </Button>
          <Button size="sm" onClick={download} disabled={busy} data-testid="audit-export-download">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
